import {
  Component,
  OnInit,
  ChangeDetectorRef,
  Output,
  Input,
  EventEmitter
} from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { FormBuilder, FormGroup, FormArray, Validators } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { CpMarketplaceApiService } from '../../services/cp-marketplace-api.service';
import {
  getTransformedDate,
  getTransformedNumber,
  getCalendarDateObj,
  amountFormatter
} from '../../utils';
import * as moment from 'moment';
import { ISSUER_MESSAGES, COMMON_MESSAGES } from 'src/app/messages';
import { forkJoin } from 'rxjs';
import { CreditRating, BoardResolution } from '../global-meta-data';

@Component({
  selector: 'app-credit-rating-details',
  templateUrl: './credit-rating-details.component.html',
  styleUrls: ['./credit-rating-details.component.scss']
})
export class CreditRatingDetailsComponent implements OnInit {
  image_file = '';
  fileTypes = ['.doc', '.docx', '.pdf'];
  creditRatingForm: FormGroup;
  creditListing: FormArray;
  isDocsUpdated = false;
  updatedDocs: any = {};
  uploadedFileName: String = '';
  brRecordId;
  additionalAttachment;
  today = getCalendarDateObj(new Date());

  public CREDIT_RATING_AGENCIES: any = ['CRISIL', 'ICRA', 'CARE', 'BRICKWORK(BWR)', 'INDIA RATINGS', 'Infomerics Valuation And Rating Pvt Ltd', 'Acuite Ratings And Research Limited'];

  public CREDIT_RATING_SHORT_TERM: any = ['A1+', 'A2+', 'A3+', 'A4+', 'A1', 'A2', 'A3',
    'A4', 'D', 'A1+(SO)', 'A2+(SO)', 'A3+(SO)', 'A4+(SO)', 'A1(SO)', 'A2(SO)', 'A3(SO)',
    'A4(SO)', 'D(SO)'];

  public valueAmountIn: any = [
    { amount: 'Cr' },
    { amount: 'Lakhs' }
  ];

  @Input() issueValDate;
  @Output() formvalue = new EventEmitter();
  formSubmittedInvalid = false;

  constructor(
    private fb: FormBuilder,
    public activeModal: NgbActiveModal,
    private apiService: CpMarketplaceApiService,
    private toastr: ToastrService
  ) { }

  ngOnInit() {
    this.creditRatingForm = this.fb.group({
      creditListing: this.fb.array([])
    });

    this.fetchCreditRatingModalData();
  }

  /**
   * Fetches all the data required to be shown on the credit ratings modal.
   * Currently fetches Credit Ratings and Board Resolution data.
   */
  fetchCreditRatingModalData() {
    forkJoin([
      this.apiService.getCreditRatings(),
      this.apiService.getBoardResolution()
    ]).subscribe(([creditRatingResp, brResp]) => {
      this.loadBoardResolution(brResp as BoardResolution[]);
      this.loadCreditRatings(creditRatingResp as CreditRating[]);
    });
  }

  /**
   * For the given existing credit ratings, fills the form data with the existing data. Else, shows an
   * empty form on the UI.
   * @param creditRatings - List of existing credit ratings receieved from the API response.
   */
  loadCreditRatings(existingCreditRatings: CreditRating[]) {
    const creditList = this.creditRatingForm.get('creditListing') as FormArray;

    if (existingCreditRatings && existingCreditRatings.length) {
      existingCreditRatings.forEach((creditRating) => {
        creditList.push(this.createCreditRatingDetails(creditRating));
      });
    } else {
      creditList.push(this.createCreditRatingDetails({}));
    }
  }

  /**
   * For the given board resolution, sets the brRecordId variable.
   * @param boardResolution - Board Resolution response from the backend
   */
  loadBoardResolution(boardResolution: BoardResolution[]) {
    if (boardResolution.length) {
      this.brRecordId = boardResolution[0].id;
    }
  }

  createCreditRatingDetails(creditRatingObj): FormGroup {
    const dateRating = creditRatingObj.date_of_credit_rating ?
      getCalendarDateObj(moment(creditRatingObj.date_of_credit_rating).toDate()) :
      null;

    const ratingExpiryDate = creditRatingObj.validity_period_for_rating_expiry_date ?
      getCalendarDateObj(moment(creditRatingObj.validity_period_for_rating_expiry_date).toDate()) :
      null;

    const issuanceExpiryDate = creditRatingObj.validity_for_issuance_expiry_date ?
      getCalendarDateObj(moment(creditRatingObj.validity_for_issuance_expiry_date).toDate()) :
      null;

    const amount = creditRatingObj.amount ?
      amountFormatter(parseInt(creditRatingObj.amount, 10), false, 'object') :
      {};

    return this.fb.group({
      name_of_credit_rating_agency: [creditRatingObj.name_of_credit_rating_agency || null, Validators.required],
      credit_rating: [creditRatingObj.credit_rating || null, Validators.required],
      date_of_credit_rating: [dateRating, Validators.required],
      validity_period_for_rating_expiry_date: [ratingExpiryDate, Validators.required],
      validity_for_issuance_expiry_date: [issuanceExpiryDate, Validators.required],
      validity_for_issuance: [creditRatingObj.validity_for_issuance || null, Validators.required],
      validity_period_for_rating: [creditRatingObj.validity_period_for_rating || null, Validators.required],
      amountValue: [amount.reducedValue || null, Validators.required],
      amountValueIn: [amount.denomination || 'Cr', Validators.required],
      conditions_if_any: [creditRatingObj.conditions_if_any || null, Validators.required],
      credit_rating_document: [
        creditRatingObj.credit_rating_document ?
          (creditRatingObj.credit_rating_document.file_key || null) :
          null, Validators.required],
      credit_rating_file_url: creditRatingObj.credit_rating_document ? creditRatingObj.credit_rating_document.file_url : null,
      credit_support: ['yes', Validators.required],
      docdata_board_resolution: this.brRecordId
    });
  }

  /**
   * Adds empty an empty credit rating on the UI.
   */
  addCreditLetter() {
    this.creditListing = this.creditRatingForm.get('creditListing') as FormArray;

    this.creditListing.push(this.createCreditRatingDetails({}));
  }

  /**
   * Removes the credit rating at the given index.
   */
  removeCreditLetter(index) {
    this.creditListing = this.creditRatingForm.get('creditListing') as FormArray;

    this.creditListing.removeAt(index);
  }

  /**
   * Called on the Save button click.
   */
  onCreditRatingSubmit() {
    const creditListing = this.creditRatingForm.get('creditListing') as FormArray;
    if (this.creditRatingForm.invalid) {
      this.toastr.warning(ISSUER_MESSAGES.INVALID_FORM);
      this.formSubmittedInvalid = true;
    } else {
      this.formSubmittedInvalid = false;
      const creditRatings = this.creditRatingForm.value.creditListing.
        map((creditListingRow) => {
          const retObj = Object.assign({}, creditListingRow, {
            date_of_credit_rating: getTransformedDate(creditListingRow.date_of_credit_rating, null, false),
            validity_period_for_rating_expiry_date:
            getTransformedDate(creditListingRow.validity_period_for_rating_expiry_date, null, false),
            validity_for_issuance_expiry_date: getTransformedDate(creditListingRow.validity_for_issuance_expiry_date, null, false),
            amount: getTransformedNumber(creditListingRow.amountValue, creditListingRow.amountValueIn)
          });

          delete retObj.amountValue;
          delete retObj.amountValueIn;
          delete retObj.credit_rating_file_url;
          return retObj;
        });

      this.apiService.creditRatingUpdate(creditRatings).subscribe((resp: CreditRating[]) => {
        if (resp.length) {
          this.toastr.success(ISSUER_MESSAGES.CREDIT_RATING_SAVE);
          this.activeModal.close(resp);
        }
      });
    }
  }

  onUploadCreditRatingsChange(fileContent, creditRatingsRef) {
    const formDataRequest = new FormData();
    formDataRequest.append('files', fileContent);
    this.apiService.uploadFileAndGetToken(formDataRequest).subscribe(resp => {
      if (resp) {
        this.toastr.success('Credit Rating Letter ' + COMMON_MESSAGES.FILE_UPLOADED_SUCCESSFULLY);
        // uploadFileAndGetToken api will always return an array of files.
        // Since we are sending only one document, we are reading only the first document from resp.
        creditRatingsRef.controls.credit_rating_document.setValue(resp[0]);
      }
    });
  }

  /**
   * For the given file id, fetches the file link and displays it in a new tab.
   */
  viewDocument(fileUrl) {
    this.apiService.fetchFile(fileUrl).subscribe((fileUrlResp: any) => {
      window.open(fileUrlResp, '_blank');
    });
  }
}
